import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, FolderKanban } from "lucide-react";

const STATUS_COLOR = {
  active: "bg-green-100 text-green-700",
  on_hold: "bg-amber-100 text-amber-700",
  completed: "bg-slate-200 text-slate-600",
};

export default function Projects() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", status: "active", color: "#0A37D8" });

  const load = () => api.get("/projects").then((r) => setProjects(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name.trim()) return;
    await api.post("/projects", form);
    setOpen(false);
    setForm({ name: "", description: "", status: "active", color: "#0A37D8" });
    toast.success("Project created");
    load();
  };

  return (
    <div className="space-y-6" data-testid="projects-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="label-mono text-slate-500">Initiatives</div>
          <h1 className="text-4xl font-black tracking-tighter">Projects</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-sm bg-slate-950 hover:bg-slate-800" data-testid="new-project-button">
              <Plus size={16} className="mr-2" /> New Project
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm">
            <DialogHeader><DialogTitle>New Project</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input placeholder="Project name" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" data-testid="project-name-input" />
              <Textarea placeholder="Description" value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm" />
              <div className="flex items-center gap-3">
                <span className="label-mono text-slate-500">Accent</span>
                <input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="w-10 h-8 rounded-sm border border-border" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={create} className="rounded-sm bg-slate-950 hover:bg-slate-800" data-testid="save-project-button">Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {projects.length === 0 && <p className="text-sm text-muted-foreground">No projects yet. Create your first initiative.</p>}
        {projects.map((p) => {
          const pct = p.task_count ? Math.round((p.done_count / p.task_count) * 100) : 0;
          return (
            <Card key={p.id} onClick={() => navigate(`/projects/${p.id}`)}
              className="rounded-sm border-border shadow-none p-5 cursor-pointer hover:border-slate-400 transition-colors"
              data-testid={`project-${p.id}`}>
              <div className="flex items-start justify-between">
                <div className="w-9 h-9 rounded-sm flex items-center justify-center text-white" style={{ background: p.color }}>
                  <FolderKanban size={18} />
                </div>
                <span className={`label-mono px-2 py-0.5 rounded-sm ${STATUS_COLOR[p.status]}`}>{p.status.replace("_", " ")}</span>
              </div>
              <h3 className="text-xl font-bold tracking-tight mt-4">{p.name}</h3>
              <p className="text-sm text-slate-600 mt-1 line-clamp-2 min-h-[40px]">{p.description || "No description"}</p>
              <div className="mt-4">
                <div className="flex justify-between label-mono text-slate-500 mb-1.5">
                  <span>{p.done_count}/{p.task_count} tasks</span><span>{pct}%</span>
                </div>
                <Progress value={pct} className="h-1.5 rounded-sm" />
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
